import React, { useState, useEffect } from 'react';
import { useCachedAvatarUrl } from '../hooks/useCachedAvatarUrl';
import { getInitials, getInitialsColor } from '../utils/nameFormat';
import './CachedAvatar.css';

/**
 * Renders an avatar image using a cached URL to avoid repeated requests to external
 * hosts (e.g. Google profile images) and prevent 429 Too Many Requests.
 * When the image is 429/unavailable or fails to load, shows initials from optional
 * `name` prop (automatic profile photo fallback).
 */
export default function CachedAvatar({ url, name, className, alt = '' }) {
  const resolvedUrl = useCachedAvatarUrl(url);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [url, resolvedUrl]);

  const showInitials = (!resolvedUrl || imageError);
  const initials = name ? getInitials(name) : '?';
  const initialsColor = name ? getInitialsColor(name) : 'hsl(0, 0%, 45%)';

  if (!url && !name) return null;

  if (showInitials) {
    return (
      <span
        className={`cached-avatar-initials ${className || ''}`.trim()}
        style={{ backgroundColor: initialsColor }}
        aria-hidden={!alt}
        title={alt || undefined}
      >
        {initials}
      </span>
    );
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      className={className}
      onError={() => setImageError(true)}
    />
  );
}
